import type { SessionEnvelope } from "@bianhua/protocol";
import type { ArtifactSync } from "./artifact-sync";
import type { RuntimeConfig } from "./config";
import type { ArtifactManifest } from "./globals";

type WireManifest = {
  artifactId: string;
  projectId: string;
  fileName: string;
  relativePath: string;
  mediaType: string;
  size: number;
  sha256: string;
  transfer: { mode: "chunks"; chunkSize: number; chunkCount: number } | { mode: "url" };
  retention: { serverStored: boolean; objectKey?: string; retainedUntil?: string };
  createdAt: string;
};

type WireChunk = {
  artifactId: string;
  index: number;
  offset: number;
  dataBase64: string;
  chunkSha256: string;
  final: boolean;
};

type WireUrl = { artifactId: string; url: string; expiresAt: string; sha256: string };

type PendingArtifact = {
  manifest?: WireManifest;
  url?: WireUrl;
  chunks: Map<number, { offset: number; bytes: Uint8Array }>;
  processing: boolean;
};

function decodeBase64(input: string): Uint8Array {
  const binary = atob(input);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

async function sha256(bytes: Uint8Array): Promise<string> {
  const copy = Uint8Array.from(bytes);
  const hash = await crypto.subtle.digest("SHA-256", copy.buffer);
  return Array.from(new Uint8Array(hash), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export class ArtifactReceiver extends EventTarget {
  private readonly sync: ArtifactSync;
  private readonly config: RuntimeConfig;
  private readonly pending = new Map<string, PendingArtifact>();
  private generation = 0;

  constructor(sync: ArtifactSync, config: RuntimeConfig) {
    super();
    this.sync = sync;
    this.config = config;
  }

  resetForPrincipalChange(): void {
    this.generation += 1;
    this.pending.clear();
  }

  handle(envelope: SessionEnvelope): void {
    const generation = this.generation;
    if (envelope.kind === "artifact.manifest") {
      const manifest = envelope.payload as unknown as WireManifest;
      if (manifest.projectId !== this.config.projectId) {
        this.fail(manifest.artifactId, new Error("产物清单不属于当前项目，已拒绝保存"), generation);
        return;
      }
      const state = this.state(manifest.artifactId);
      state.manifest = manifest;
      this.tryComplete(manifest.artifactId, generation).catch((error) => this.fail(manifest.artifactId, error, generation));
    }
    if (envelope.kind === "artifact.url") {
      const url = envelope.payload as unknown as WireUrl;
      const state = this.state(url.artifactId);
      state.url = url;
      this.tryComplete(url.artifactId, generation).catch((error) => this.fail(url.artifactId, error, generation));
    }
    if (envelope.kind === "artifact.chunk") {
      this.acceptChunk(envelope.payload as unknown as WireChunk, generation).catch((error) => {
        const artifactId = String((envelope.payload as Record<string, unknown>).artifactId || "unknown");
        this.fail(artifactId, error, generation);
      });
    }
  }

  private state(artifactId: string): PendingArtifact {
    let state = this.pending.get(artifactId);
    if (!state) {
      state = { chunks: new Map(), processing: false };
      this.pending.set(artifactId, state);
    }
    return state;
  }

  private async acceptChunk(chunk: WireChunk, generation: number): Promise<void> {
    const bytes = decodeBase64(chunk.dataBase64);
    if (await sha256(bytes) !== chunk.chunkSha256.toLowerCase()) throw new Error("产物分块 SHA-256 校验失败");
    if (generation !== this.generation) return;
    const state = this.state(chunk.artifactId);
    const existing = state.chunks.get(chunk.index);
    if (existing && (existing.offset !== chunk.offset || await sha256(existing.bytes) !== chunk.chunkSha256.toLowerCase())) {
      throw new Error("服务器重复发送了内容不一致的产物分块");
    }
    state.chunks.set(chunk.index, { offset: chunk.offset, bytes });
    await this.tryComplete(chunk.artifactId, generation);
  }

  private async tryComplete(artifactId: string, generation: number): Promise<void> {
    if (generation !== this.generation) return;
    const state = this.state(artifactId);
    const manifest = state.manifest;
    if (!manifest || state.processing) return;
    if (!manifest.retention?.serverStored) throw new Error("服务器未确认保留产物副本，已拒绝自动镜像");
    if (manifest.size > 512 * 1024 * 1024) throw new Error("单个产物超过客户端 512 MB 上限");
    state.processing = true;
    try {
      let localManifest: ArtifactManifest;
      if (manifest.transfer.mode === "url") {
        if (!state.url) return;
        if (state.url.sha256.toLowerCase() !== manifest.sha256.toLowerCase()) throw new Error("产物 URL 与清单的 SHA-256 不一致");
        if (Date.parse(state.url.expiresAt) <= Date.now()) throw new Error("产物下载地址已经过期");
        localManifest = {
          artifactId,
          projectName: this.config.projectName,
          relativePath: manifest.relativePath,
          sizeBytes: manifest.size,
          sha256: manifest.sha256,
          mimeType: manifest.mediaType,
          download: { url: state.url.url, expiresAt: state.url.expiresAt },
        };
      } else {
        if (state.chunks.size !== manifest.transfer.chunkCount) return;
        const ordered = Array.from(state.chunks.entries()).sort(([left], [right]) => left - right);
        let expectedOffset = 0;
        const bytes = new Uint8Array(manifest.size);
        for (const [index, chunk] of ordered) {
          if (index < 0 || index >= manifest.transfer.chunkCount || chunk.offset !== expectedOffset) {
            throw new Error("产物分块序号或偏移不连续");
          }
          if (expectedOffset + chunk.bytes.length > bytes.length) throw new Error("产物分块超过清单声明大小");
          bytes.set(chunk.bytes, expectedOffset);
          expectedOffset += chunk.bytes.length;
        }
        if (expectedOffset !== manifest.size) throw new Error("产物分块总大小与清单不一致");
        if (await sha256(bytes) !== manifest.sha256.toLowerCase()) throw new Error("产物总 SHA-256 校验失败");
        localManifest = {
          artifactId,
          projectName: this.config.projectName,
          relativePath: manifest.relativePath,
          sizeBytes: manifest.size,
          sha256: manifest.sha256,
          mimeType: manifest.mediaType,
          bytes,
        };
      }
      if (generation !== this.generation) return;
      const result = await this.sync.saveManifest(localManifest);
      if (generation !== this.generation) return;
      this.dispatchEvent(new CustomEvent("complete", { detail: { artifactId, manifest, result } }));
      this.pending.delete(artifactId);
    } finally {
      const current = this.pending.get(artifactId);
      if (generation === this.generation && current === state) current.processing = false;
    }
  }

  private fail(artifactId: string, error: unknown, generation = this.generation): void {
    if (generation !== this.generation) return;
    this.pending.delete(artifactId);
    this.dispatchEvent(new CustomEvent("error", { detail: { artifactId, error } }));
  }
}
